import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Clock, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/misc'
import { Dropzone } from './preview/_components/dropzone'
import { PreviewResults } from './preview/_components/preview-results'
import {
  commitPreview,
  discardPreview,
  getPreview,
  getPreviewText,
  listPreviews,
  newSessionId,
  runPreview,
} from '@/lib/api/previews/run-preview'
import { apiErrorMessage, traceIdOf } from '@/lib/api-client'
import { rememberedCollectionId } from '@/hooks/use-active-collection'
import { cn, formatDateTime } from '@/lib/utils'
import { translateEnum } from '@/lib/i18n'
import { useLocalizedPath } from '@/lib/i18n/routes'
import { type PreviewProgress } from '@/types/preview'

export function PreviewPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const path = useLocalizedPath()
  // The session id lives in the URL, so a run is linkable and back/forward work.
  const { sessionId } = useParams<{ sessionId?: string }>()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  // Collection to pre-select as the commit target (set when arriving from a
  // collection's document view).
  // Explicit ?kogu= wins; otherwise fall back to the collection the user
  // was last working in, so committing lands where they expect.
  const targetCollection = Number(searchParams.get('kogu')) || rememberedCollectionId()

  const [domains, setDomains] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  // The id is minted before the upload starts, so the URL is shareable and
  // refresh-safe from the first byte. While this session is still running there
  // is nothing to fetch yet, so the page renders progress instead.
  const [run, setRun] = useState<{ sid: string; progress: PreviewProgress } | null>(null)
  // Lemmas from the run before the latest rerun, to mark what the prompt change added.
  const [prevLemmas, setPrevLemmas] = useState<Set<string> | null>(null)
  const isRunningThis = !!run && run.sid === sessionId

  const previews = useQuery({ queryKey: ['previews'], queryFn: listPreviews })

  // Loaded from the server rather than kept in state, so a shared link or a
  // reload shows the same thing as the tab that ran it.
  const preview = useQuery({
    queryKey: ['preview', sessionId],
    queryFn: () => getPreview(sessionId!),
    enabled: !!sessionId && !isRunningThis,
    retry: false,
  })
  const text = useQuery({
    queryKey: ['preview-text', sessionId],
    queryFn: () => getPreviewText(sessionId!),
    enabled: !!sessionId && !isRunningThis,
    retry: false,
  })

  useEffect(() => () => abortRef.current?.abort(), [])

  // A file handed over from the documents view: start its run once.
  const handedFile = (location.state as { file?: File } | null)?.file
  useEffect(() => {
    if (handedFile && !busy && !sessionId) {
      navigate(location.pathname + location.search, { replace: true, state: null })
      handleFile(handedFile)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handedFile])

  // A different preview is a different baseline; drop the previous diff.
  useEffect(() => { setPrevLemmas(null) }, [sessionId])

  const handleFile = useCallback(
    async (file: File) => {
      const sid = newSessionId()
      setBusy(true)
      setError(null)
      setRun({ sid, progress: { stage: 'init', percentage: 0, message: t('preview.starting') } })
      // Straight into the URL: the run is linkable while it is still going.
      navigate(path('preview', sid))
      abortRef.current = new AbortController()
      try {
        const res = await runPreview(
          file,
          { title: file.name.replace(/\.pdf$/i, ''), domains, sessionId: sid },
          (p) => setRun((cur) => (cur && cur.sid === sid ? { sid, progress: p } : cur)),
          abortRef.current.signal,
        )
        qc.setQueryData(['preview', sid], res)
        qc.invalidateQueries({ queryKey: ['previews'] })
        toast.success(t('preview.termsFound', { count: res.term_count }), {
          description: t('preview.reviewHint'),
        })
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          setError(e instanceof Error ? e.message : apiErrorMessage(e))
          navigate(path('preview'))
        }
      } finally {
        setBusy(false)
        setRun(null)
      }
    },
    [domains, navigate, path, qc, t],
  )

  const handleCommit = useCallback(
    async (sid: string, collectionId: number | null = null) => {
      setBusy(true)
      try {
        await commitPreview(sid, collectionId)
        toast.success(t('preview.committed'), { description: t('preview.committedHint') })
        qc.invalidateQueries({ queryKey: ['previews'] })
        qc.invalidateQueries({ queryKey: ['terms'] })
        qc.invalidateQueries({ queryKey: ['domains'] })
        qc.invalidateQueries({ queryKey: ['collections'] })
        navigate(path('preview'))
      } catch (e) {
        const tid = traceIdOf(e)
        toast.error(t('preview.commitFailed'), {
          description:
            apiErrorMessage(e) + (tid ? ` · ${t('common.traceId', { id: tid })}` : ''),
        })
      } finally {
        setBusy(false)
      }
    },
    [navigate, path, qc, t],
  )

  const handleDiscard = useCallback(
    async (sid: string) => {
      setBusy(true)
      try {
        await discardPreview(sid)
        toast.success(t('preview.discarded'))
        qc.invalidateQueries({ queryKey: ['previews'] })
        navigate(path('preview'))
      } catch (e) {
        toast.error(t('preview.discardFailed'), { description: apiErrorMessage(e) })
      } finally {
        setBusy(false)
      }
    },
    [navigate, path, qc, t],
  )

  if (sessionId) {
    if (isRunningThis) {
      return (
        <div className="flex-1 p-6">
          <div className="mx-auto max-w-2xl space-y-3">
            <div>
              <h2 className="text-base font-semibold">{t('preview.processing')}</h2>
              <p className="mt-0.5 text-sm text-muted-foreground">{t('preview.processingHint')}</p>
            </div>
            <ProgressBar progress={run!.progress} />
          </div>
        </div>
      )
    }
    if (preview.isLoading) {
      return (
        <div className="space-y-3 p-6">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      )
    }
    if (preview.error || !preview.data) {
      return (
        <div className="p-6">
          <div className="mx-auto flex max-w-lg items-start gap-2 rounded-md border border-danger/30 bg-danger/10 p-3 text-sm text-danger">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <div>
              <p className="font-medium">{t('preview.notFound')}</p>
              <p className="mt-0.5 text-xs">{t('preview.notFoundHint')}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => navigate(path('preview'))}
              >
                {t('common.back')}
              </Button>
            </div>
          </div>
        </div>
      )
    }
    return (
      <PreviewResults
        result={preview.data}
        defaultCollectionId={targetCollection}
        chunks={text.data ?? []}
        textLoading={text.isLoading}
        busy={busy}
        domains={domains}
        onCommit={(cid) => handleCommit(sessionId, cid ?? targetCollection)}
        onDiscard={() => handleDiscard(sessionId)}
        onBack={() => navigate(path('preview'))}
        onRerun={(res) => {
          setPrevLemmas(
            new Set((preview.data?.terms ?? []).map((t) => t.lemma.toLowerCase())),
          )
          qc.setQueryData(['preview', sessionId], res)
          qc.invalidateQueries({ queryKey: ['previews'] })
        }}
        previousLemmas={prevLemmas ?? undefined}
      />
    )
  }

  return (
    <div className="scrollbar-thin flex-1 overflow-y-auto p-6">
      <div className="mx-auto max-w-2xl space-y-4">
        <div>
          <h2 className="text-base font-semibold">{t('preview.heading')}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">{t('preview.intro')}</p>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            {t('preview.domains')}{' '}
            <span className="font-normal">{t('preview.domainsOptional')}</span>
          </label>
          <Input
            value={domains}
            onChange={(e) => setDomains(e.target.value)}
            placeholder={t('preview.domainsPlaceholder')}
            disabled={busy}
          />
        </div>

        <Dropzone onFile={handleFile} busy={busy} />

        {run && <ProgressBar progress={run.progress} />}

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-danger/30 bg-danger/10 p-3 text-xs text-danger">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!!previews.data?.length && (
          <div className="rounded-md border border-border">
            <div className="border-b border-border px-3 py-2 text-xs font-medium text-muted-foreground">
              {t('preview.pending', { count: previews.data.length })}
            </div>
            {previews.data.map((p) => (
              <div
                key={p.session_id}
                className="flex items-center gap-2 border-b border-border/60 px-3 py-2 last:border-0"
              >
                <button
                  type="button"
                  onClick={() => navigate(path('preview', p.session_id))}
                  className="min-w-0 flex-1 text-left"
                >
                  <p className="truncate text-sm font-medium hover:underline">{p.title}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {t('units.terms', { count: p.term_count })} ·{' '}
                    {t('units.mentions', { count: p.mention_count })}
                    {p.expires_at && (
                      <>
                        {' · '}
                        <Clock className="inline size-3" />{' '}
                        {t('preview.expires', { date: formatDateTime(p.expires_at) })}
                      </>
                    )}
                  </p>
                </button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => handleCommit(p.session_id)}
                >
                  {t('preview.commit')}
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  disabled={busy}
                  onClick={() => handleDiscard(p.session_id)}
                  title={t('preview.discard')}
                >
                  <Trash2 />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function ProgressBar({ progress }: { progress: PreviewProgress }) {
  const pct = progress.percentage < 0 ? null : progress.percentage
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="mb-1.5 flex items-center justify-between text-xs">
        <span className="font-medium">
          {translateEnum('stages', progress.stage)}
          <Badge variant="default" className="ml-2 font-normal">
            {progress.message}
          </Badge>
        </span>
        <span className="tabular-nums text-muted-foreground">{pct === null ? '…' : `${pct}%`}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            'h-full rounded-full bg-primary transition-all duration-300',
            pct === null && 'animate-pulse',
          )}
          style={{ width: `${pct ?? 100}%` }}
        />
      </div>
    </div>
  )
}
